import {
  Direction,
  type TicketQuery,
} from "@lytra/frontend-common/__generated__/graphql";
import { personName } from "./ticketHelpers";

/** One message of the Ticket query: the generated type, so a query change shows up here. */
export type TicketMessage = NonNullable<
  TicketQuery["conversationOne"]
>["messages"]["edges"][number]["node"];

/** The ticket timeline: a message on its own, or a note thread (root note plus its replies). */
export type TimelineItem<T> =
  { kind: "message"; message: T } | { kind: "thread"; root: T; replies: T[] };

/**
 * True for rows that are internal: a note, a staff-to-staff mail or a connector's ticket
 * note. Such a row is never a target for a customer reply (D-16).
 */
export function isInternalRow(message: {
  isInternal?: boolean | null;
  direction?: Direction | null;
}): boolean {
  return (
    Boolean(message.isInternal) || message.direction === Direction.Internal
  );
}

/**
 * Whether a row of the thread offers "An Kollegen senden": every non-internal message, inbound
 * and outbound, but never a note, a send entry or a legacy internal row (D-05, UI-SPEC 1).
 */
export function canForwardMessage(
  message: Pick<
    TicketMessage,
    "isNote" | "isForward" | "isInternal" | "direction"
  >,
): boolean {
  return !isInternalRow(message) && !message.isNote && !message.isForward;
}

/**
 * The message "An Kollegen senden" at the bottom of the thread shares: the newest one that
 * `canForwardMessage` allows, inbound or outbound. Undefined when the thread has none.
 */
export function latestForwardableMessage<
  T extends Pick<
    TicketMessage,
    "isNote" | "isForward" | "isInternal" | "direction"
  >,
>(messages: readonly T[]): T | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (canForwardMessage(messages[i])) return messages[i];
  }
  return undefined;
}

/**
 * The message a reply to the customer answers by default: the last one that is not an
 * internal row, so a note can never become the default customer reply target (D-03).
 */
export function lastCustomerReplyTarget(
  messages: readonly Pick<TicketMessage, "id" | "isInternal" | "direction">[],
): string | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (!isInternalRow(messages[i])) return messages[i].id;
  }
  return undefined;
}

interface Groupable {
  id: string;
  isNote: boolean;
  isForward?: boolean | null;
  isInternal?: boolean | null;
  direction?: Direction | null;
  parentMessageId?: string | null;
  deletedAt?: unknown;
}

/**
 * The timeline in the server's order (`receivedAt`), with every root note and every send to
 * colleagues turned into one thread item that holds its replies of any depth, flat and in
 * the same order (D-13, D-17b, #717 D-07, D-12). A thread sits where its root sits.
 *
 * A reply is an internal row (a note, or a colleague's mail reply) whose parent chain, walked
 * through internal rows only, reaches a note or a send. Customer mails, outbound replies and
 * legacy internal rows whose chain reaches neither stay single items, exactly as before.
 *
 * Deleted notes: a deleted root with at least one reply stays (the card shows the
 * placeholder), a deleted root without replies is left out, and so is a deleted reply
 * that no live reply answers. A note or send whose parent is missing from the list or is
 * not an internal row counts as a root. A deleted reply that has live replies below it
 * stays, as the placeholder they answer.
 */
export function groupTimeline<T extends Groupable>(
  messages: readonly T[],
): TimelineItem<T>[] {
  const byId = new Map(messages.map((message) => [message.id, message]));

  // Who may sit in a thread: a note, a send, or any other internal row (a mail reply).
  const isThreadMember = (message: T): boolean =>
    message.isNote || Boolean(message.isForward) || isInternalRow(message);
  // Who may be a thread's root: only a note or a send. A legacy internal row never is.
  const isRootKind = (message: T): boolean =>
    message.isNote || Boolean(message.isForward);

  // A parent counts only when both ends are thread members.
  const parentMember = (message: T): T | undefined => {
    if (!isThreadMember(message) || !message.parentMessageId) return undefined;
    const parent = byId.get(message.parentMessageId);
    return parent && isThreadMember(parent) ? parent : undefined;
  };

  // Root of a message: the topmost note or send on its parent chain, or undefined when the
  // chain reaches none (a single item). The visited set stops a cycle that bad data could
  // form; a root-kind message in a cycle is its own root.
  const rootIdOf = (message: T): string | undefined => {
    const seen = new Set<string>([message.id]);
    let anchor = isRootKind(message) ? message.id : undefined;
    let current = message;
    for (;;) {
      const parent = parentMember(current);
      if (!parent) return anchor;
      if (seen.has(parent.id)) return isRootKind(message) ? message.id : anchor;
      seen.add(parent.id);
      if (isRootKind(parent)) anchor = parent.id;
      current = parent;
    }
  };

  const repliesByRoot = new Map<string, T[]>();
  for (const message of messages) {
    const rootId = rootIdOf(message);
    if (rootId === undefined || rootId === message.id) continue;
    const list = repliesByRoot.get(rootId);
    if (list) {
      list.push(message);
    } else {
      repliesByRoot.set(rootId, [message]);
    }
  }

  // A deleted reply is only shown as a placeholder for the live replies below it.
  const keepReply = (
    reply: T,
    replies: readonly T[],
    visited: ReadonlySet<string> = new Set(),
  ): boolean => {
    if (!reply.deletedAt) return true;
    const path = new Set(visited).add(reply.id);
    return replies.some(
      (other) =>
        other.parentMessageId === reply.id &&
        !path.has(other.id) &&
        keepReply(other, replies, path),
    );
  };

  const items: TimelineItem<T>[] = [];
  for (const message of messages) {
    const rootId = rootIdOf(message);
    if (rootId === undefined) {
      items.push({ kind: "message", message });
      continue;
    }
    if (rootId !== message.id) continue; // a reply, placed under its root
    const all = repliesByRoot.get(message.id) ?? [];
    const replies = all.filter((reply) => keepReply(reply, all));
    if (message.deletedAt && replies.length === 0) continue;
    items.push({ kind: "thread", root: message, replies });
  }
  return items;
}

/** The id of the thread root that holds `messageId` (the root itself or one of its replies), for the deep link. */
export function threadRootOf<T extends { id: string }>(
  items: readonly TimelineItem<T>[],
  messageId: string,
): string | undefined {
  for (const item of items) {
    if (item.kind !== "thread") continue;
    if (
      item.root.id === messageId ||
      item.replies.some((reply) => reply.id === messageId)
    ) {
      return item.root.id;
    }
  }
  return undefined;
}

/** What the quote line of a send entry names: who wrote the forwarded message, and when. */
export interface ForwardSource {
  /** The sender's name, else the address; null when the message tells neither. */
  sender: string | null;
  /** ISO time of the message. */
  time: string;
}

type SourceCandidate = Pick<
  TicketMessage,
  | "id"
  | "direction"
  | "agent"
  | "contact"
  | "emailFromName"
  | "emailFromAddress"
  | "receivedAt"
  | "sentAt"
  | "createdAt"
>;

/**
 * The sender and time of the message a send forwarded, or null when it is not in the loaded
 * thread (the quote line then hides "Anzeigen", UI-SPEC 4). An outbound message was written
 * by its agent, any other by its contact or, failing that, the mail's From header.
 */
export function forwardSourceOf(
  messages: readonly SourceCandidate[],
  messageId: string,
): ForwardSource | null {
  const source = messages.find((message) => message.id === messageId);
  if (!source) return null;
  const agentName = personName(source.agent ?? undefined);
  const contactName =
    source.contact?.name?.trim() ||
    personName(source.contact ?? undefined) ||
    source.contact?.emailAddress ||
    null;
  const fromHeader =
    source.emailFromName?.trim() || source.emailFromAddress || null;
  const sender =
    source.direction === Direction.Outbound
      ? (agentName ?? fromHeader ?? contactName)
      : (contactName ?? fromHeader ?? agentName);
  return {
    sender: sender ?? null,
    time: source.receivedAt ?? source.sentAt ?? source.createdAt,
  };
}
